// Page-capture helpers for the auto bug-report flow. Provides:
// - a tiny ring buffer of the most recent fetch requests
// - an rrweb session-replay ring buffer trimmed to the last 30s
// - a screenshot helper that uses html2canvas and downscales to keep payloads small
//
// All capture is in-memory only. Nothing is persisted to localStorage so we
// don't bloat the tab over time.

import { record } from "rrweb";
import type { eventWithTime } from "@rrweb/types";

const NET_MAX = 25;
const REPLAY_WINDOW_MS = 30_000;
const REPLAY_TRIM_INTERVAL_MS = 5_000;

export type NetEntry = {
  method: string;
  url: string;
  status: number;
  ok: boolean;
  ms: number;
  at: string;
};

const netBuffer: NetEntry[] = [];
let netInstalled = false;

const replayBuffer: eventWithTime[] = [];
let replayInstalled = false;
let stopReplay: (() => void) | null = null;

function pushNet(entry: NetEntry) {
  netBuffer.push(entry);
  if (netBuffer.length > NET_MAX) netBuffer.shift();
}

/** Wraps window.fetch to record url/status/duration of each request. */
export function installNetworkCapture() {
  if (netInstalled || typeof window === "undefined" || !window.fetch) return;
  netInstalled = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const started = performance.now();
    const method = (init?.method || (typeof input !== "string" && "method" in input ? input.method : "GET") || "GET").toUpperCase();
    let url = "";
    try {
      url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
    } catch {
      url = "<unknown>";
    }
    try {
      const res = await orig(input as RequestInfo, init);
      pushNet({
        method,
        url: url.slice(0, 500),
        status: res.status,
        ok: res.ok,
        ms: Math.round(performance.now() - started),
        at: new Date().toISOString(),
      });
      return res;
    } catch (err) {
      pushNet({
        method,
        url: url.slice(0, 500),
        status: 0,
        ok: false,
        ms: Math.round(performance.now() - started),
        at: new Date().toISOString(),
      });
      throw err;
    }
  };
}

export function getNetworkLog(): NetEntry[] {
  return netBuffer.slice();
}

/** Starts rrweb recording into a rolling 30s in-memory buffer. */
export function installSessionReplay() {
  if (replayInstalled || typeof window === "undefined") return;
  replayInstalled = true;
  try {
    stopReplay = record({
      emit(event) {
        replayBuffer.push(event);
      },
      // Keep payload small — block media/canvas snapshots.
      sampling: { mousemove: 100, scroll: 150, input: "last" },
      maskAllInputs: true,
      maskTextSelector: "[data-rrweb-mask], input, textarea",
    }) || null;
    // Trim anything older than the window every few seconds.
    setInterval(() => {
      const cutoff = Date.now() - REPLAY_WINDOW_MS;
      while (replayBuffer.length && replayBuffer[0].timestamp < cutoff) {
        replayBuffer.shift();
      }
    }, REPLAY_TRIM_INTERVAL_MS);
  } catch {
    // rrweb can throw on exotic browsers — bug reporting must never break the page.
  }
}

export function getReplayEvents(): eventWithTime[] {
  return replayBuffer.slice();
}

export function stopSessionReplay() {
  if (stopReplay) {
    try { stopReplay(); } catch { /* noop */ }
    stopReplay = null;
  }
}

/** Take a viewport screenshot, downscaled to <=1280px wide JPEG. */
export async function captureScreenshot(): Promise<Blob | null> {
  if (typeof window === "undefined") return null;
  try {
    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(document.body, {
      backgroundColor: null,
      useCORS: true,
      logging: false,
      scale: Math.min(1, 1280 / Math.max(window.innerWidth, 1)),
      ignoreElements: (el) => el.hasAttribute("data-rrweb-mask") || el.tagName === "VIDEO",
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
    });
    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.7);
    });
  } catch {
    return null;
  }
}